import { Popover } from "@mui/material";
import * as React from "react";

import type { FilterMatchMode } from "@/shared/components/filters/FilterSchema";
import { ChevronDown } from "@/shared/foundation/icons";
import { usePopupState } from "@/shared/hooks/usePopupState";

import styles from "./MatchModePicker.module.css";

export interface MatchModePickerProps {
  mode: FilterMatchMode;
  onChange: (next: FilterMatchMode) => void;
}

/**
 * Leading "Match all / Match any" picker in `<ResourceView.ActiveFilters>`.
 * Trigger shows the current mode; clicking opens a small menu with the
 * AND / OR options + helper text. Single-mode — the whole filter set is
 * AND-joined or OR-joined; no per-pair nesting.
 */
export function MatchModePicker({ mode, onChange }: MatchModePickerProps) {
  const popup = usePopupState();

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    if (popup.isOpen) {
      popup.close();
    } else {
      popup.open({ anchorEl: event.currentTarget });
    }
  };

  const select = (next: FilterMatchMode) => {
    onChange(next);
    popup.close();
  };

  return (
    <>
      <button
        type="button"
        className={styles.pill}
        aria-haspopup="menu"
        aria-expanded={popup.isOpen}
        aria-label={`Match mode: ${mode === "AND" ? "all" : "any"}`}
        onClick={handleTriggerClick}
      >
        <span className={styles.label}>Match</span>
        <span className={styles.value}>{mode === "AND" ? "all" : "any"}</span>
        <ChevronDown className={styles.chevron} />
      </button>
      <Popover
        open={popup.isOpen}
        anchorEl={popup.anchor}
        onClose={popup.close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { className: styles.menu, role: "menu" } }}
      >
        <button
          type="button"
          className={styles.option}
          data-active={mode === "AND"}
          role="menuitemradio"
          aria-checked={mode === "AND"}
          onClick={() => select("AND")}
        >
          <span className={styles.optionTitle}>Match all</span>
          <span className={styles.optionHelp}>
            Every filter must match (AND)
          </span>
        </button>
        <button
          type="button"
          className={styles.option}
          data-active={mode === "OR"}
          role="menuitemradio"
          aria-checked={mode === "OR"}
          onClick={() => select("OR")}
        >
          <span className={styles.optionTitle}>Match any</span>
          <span className={styles.optionHelp}>
            At least one filter matches (OR)
          </span>
        </button>
      </Popover>
    </>
  );
}
